// Stable per-browser device id for capture payloads. The native app has a registered device id;
// Guard Web persists a generated UUID in localStorage so all of a guard's captures from this browser
// share one id. It's an opaque UUID — never user/account identity, never a secret. Storage is
// injectable so it is testable without a real localStorage.

const DEVICE_ID_KEY = "secops.guard.deviceId";

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `dev-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
}

/** Read the persisted device id, creating + storing one on first use. Falls back to an ephemeral id
 *  when storage is unavailable (e.g. private mode) — capture still works for the session. */
export function getOrCreateDeviceId(store?: KeyValueStore): string {
  const target = store ?? defaultStore();
  if (!target) {
    return newId();
  }
  const existing = target.getItem(DEVICE_ID_KEY);
  if (typeof existing === "string" && existing.length > 0) {
    return existing;
  }
  const created = newId();
  try {
    target.setItem(DEVICE_ID_KEY, created);
  } catch {
    // Storage may be unavailable / quota-exceeded; the id still works for this session.
  }
  return created;
}

function defaultStore(): KeyValueStore | undefined {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      return window.localStorage;
    }
  } catch {
    // Accessing localStorage can throw in sandboxed/blocked contexts.
  }
  return undefined;
}

export { DEVICE_ID_KEY };
